'use strict';

/**
 * Deterministic canonicalization for labor-market cache keys and Adzuna queries.
 * Identical role + city + seniority band must always produce the same key and search inputs.
 */

const ROLE_ABBREVIATION_TO_CANONICAL = Object.freeze({
  swe: 'software engineer',
  'software eng': 'software engineer',
  sde: 'software engineer',
  'software dev': 'software developer',
  'sw eng': 'software engineer',
  'sr swe': 'software engineer',
  pm: 'product manager',
  rn: 'registered nurse',
  sre: 'site reliability engineer',
  devops: 'devops engineer',
  'data sci': 'data scientist',
  'ml eng': 'machine learning engineer',
  'fe dev': 'frontend developer',
  'be dev': 'backend developer',
  'full stack dev': 'full stack developer',
  'dental hyg': 'dental hygienist',
});

/** Prefix/suffix seniority markers stripped from role title before lookup. */
const SENIORITY_PREFIX_RE =
  /^(?:(?:sr|senior|staff|principal|lead|junior|jr|assoc(?:iate)?|entry(?:[\s-]level)?)\s*\.?\s*)+/i;
const SENIORITY_SUFFIX_RE =
  /\s*(?:,?\s*(?:i{1,3}|iv|v|vi{0,3}|[1-5]|l[1-7]|ic[1-4]|individual contributor))\s*$/i;

const SENIORITY_BAND_ALIASES = Object.freeze({
  entry: 'junior',
  intern: 'junior',
  associate: 'junior',
  junior: 'junior',
  jr: 'junior',
  mid: 'mid',
  middle: 'mid',
  intermediate: 'mid',
  senior: 'senior',
  sr: 'senior',
  staff: 'senior',
  principal: 'senior',
  lead: 'senior',
  director: 'senior',
  manager: 'mid',
});

/** City/metro aliases → canonical city name (lowercase). */
const LOCATION_ALIAS_TO_CITY = Object.freeze({
  nyc: 'new york',
  'new york city': 'new york',
  'new york ny': 'new york',
  sf: 'san francisco',
  'san fran': 'san francisco',
  la: 'los angeles',
  'los angeles ca': 'los angeles',
  chi: 'chicago',
  'chi town': 'chicago',
  dfw: 'dallas',
  'dallas fort worth': 'dallas',
  'dallas-fort worth': 'dallas',
  phx: 'phoenix',
  philly: 'philadelphia',
  dc: 'washington',
  'washington dc': 'washington',
  'washington d.c.': 'washington',
  bos: 'boston',
  atl: 'atlanta',
  sea: 'seattle',
  den: 'denver',
  aus: 'austin',
  hou: 'houston',
  mia: 'miami',
  det: 'detroit',
  min: 'minneapolis',
  pdx: 'portland',
  cle: 'cleveland',
  indy: 'indianapolis',
  nash: 'nashville',
  raleigh: 'raleigh',
});

const US_STATE_SUFFIX_RE =
  /,\s*(?:alabama|alaska|arizona|arkansas|california|colorado|connecticut|delaware|florida|georgia|hawaii|idaho|illinois|indiana|iowa|kansas|kentucky|louisiana|maine|maryland|massachusetts|michigan|minnesota|mississippi|missouri|montana|nebraska|nevada|(?:new\s+)?hampshire|(?:new\s+)?jersey|(?:new\s+)?mexico|(?:new\s+)?york|north\s+carolina|north\s+dakota|ohio|oklahoma|oregon|pennsylvania|rhode\s+island|south\s+carolina|south\s+dakota|tennessee|texas|utah|vermont|virginia|washington|west\s+virginia|wisconsin|wyoming|district\s+of\s+columbia)\s*$/i;
/** Requires comma so city names ending in two letters (e.g. Dallas) are not truncated. */
const US_STATE_ABBR_SUFFIX_RE = /,\s*[a-z]{2}\s*$/i;

function collapseWhitespace(value) {
  if (value == null || value === '') return '';
  return String(value).trim().replace(/\s+/g, ' ');
}

function normalizeToken(value) {
  return collapseWhitespace(value).toLowerCase();
}

/**
 * Extract seniority band from title text and optional explicit level field.
 * @param {string} role
 * @param {string} [explicitLevel]
 * @returns {'junior'|'mid'|'senior'|''}
 */
function extractSeniorityBand(role, explicitLevel = '') {
  const levelTok = normalizeToken(explicitLevel);
  if (levelTok && SENIORITY_BAND_ALIASES[levelTok]) {
    return SENIORITY_BAND_ALIASES[levelTok];
  }
  for (const [alias, band] of Object.entries(SENIORITY_BAND_ALIASES)) {
    if (levelTok.includes(alias)) return band;
  }

  const r = normalizeToken(role);
  if (!r) return '';
  if (/\b(principal|staff|director|vp|chief)\b/.test(r)) return 'senior';
  if (/\b(senior|sr\.?|lead)\b/.test(r)) return 'senior';
  if (/\b(junior|jr\.?|entry|associate|intern)\b/.test(r)) return 'junior';
  return 'mid';
}

/**
 * Strip seniority tokens from role title for canonical job family lookup.
 * @param {string} role
 */
function stripSeniorityFromRole(role) {
  let r = collapseWhitespace(role);
  if (!r) return '';
  let prev;
  do {
    prev = r;
    r = r.replace(SENIORITY_PREFIX_RE, '').replace(SENIORITY_SUFFIX_RE, '').trim();
  } while (r !== prev && r.length > 0);
  return collapseWhitespace(r);
}

/**
 * Map abbreviations and known variants to a canonical job title (lowercase).
 * @param {string} role
 */
function canonicalizeRoleTitle(role) {
  const stripped = stripSeniorityFromRole(role);
  let tok = normalizeToken(stripped);
  if (!tok) return '';

  if (ROLE_ABBREVIATION_TO_CANONICAL[tok]) {
    return ROLE_ABBREVIATION_TO_CANONICAL[tok];
  }

  for (const [abbr, canonical] of Object.entries(ROLE_ABBREVIATION_TO_CANONICAL)) {
    if (tok === abbr || tok.startsWith(`${abbr} `) || tok.endsWith(` ${abbr}`)) {
      tok = tok.replace(abbr, canonical);
    }
  }

  return tok;
}

/**
 * Normalize work location to canonical city name (lowercase, no state suffix).
 * @param {string} location
 */
function canonicalizeWorkLocation(location) {
  let loc = normalizeToken(location);
  if (!loc) return '';

  loc = loc.replace(US_STATE_SUFFIX_RE, '').replace(US_STATE_ABBR_SUFFIX_RE, '').trim();
  if (LOCATION_ALIAS_TO_CITY[loc]) return LOCATION_ALIAS_TO_CITY[loc];

  const commaParts = loc.split(',').map((p) => p.trim()).filter(Boolean);
  if (commaParts.length >= 1) {
    const cityCandidate = commaParts[0];
    if (LOCATION_ALIAS_TO_CITY[cityCandidate]) return LOCATION_ALIAS_TO_CITY[cityCandidate];
    loc = cityCandidate;
  }

  return loc;
}

/**
 * Human-readable canonical role for Adzuna `what=` (title case words).
 * @param {string} canonicalRoleLower
 */
function displayCanonicalRole(canonicalRoleLower) {
  if (!canonicalRoleLower) return '';
  return canonicalRoleLower
    .split(' ')
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * Human-readable canonical city for Adzuna `where=`.
 * @param {string} canonicalCityLower
 */
function displayCanonicalCity(canonicalCityLower) {
  if (!canonicalCityLower) return '';
  return canonicalCityLower
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Build stable cache key: canonical_role|canonical_city|seniority_band
 * Industry is appended when present for backward-compatible four-segment keys.
 * @param {{ role?: string, workLocation?: string, jobLevel?: string, industry?: string }} input
 */
/**
 * Seniority for cache keys uses explicit job level only (not title prefixes),
 * so "Sr Software Engineer" and "Software Engineer" share one market benchmark.
 */
function extractSeniorityBandForCache(_role, explicitLevel = '') {
  const levelTok = normalizeToken(explicitLevel);
  if (!levelTok) return 'mid';
  if (SENIORITY_BAND_ALIASES[levelTok]) return SENIORITY_BAND_ALIASES[levelTok];
  for (const [alias, band] of Object.entries(SENIORITY_BAND_ALIASES)) {
    if (levelTok.includes(alias)) return band;
  }
  return 'mid';
}

function buildCanonicalMarketKey(input = {}) {
  const canonicalRole = canonicalizeRoleTitle(input.role || '');
  const canonicalCity = canonicalizeWorkLocation(input.workLocation || input.location || '');
  const band = extractSeniorityBandForCache('', input.jobLevel || input.level || '');
  const industry = normalizeToken(input.industry || '');
  const segments = [canonicalRole, canonicalCity, band];
  if (industry) segments.push(industry);
  return segments.join('|');
}

/**
 * Full canonical market inputs for API + cache layers.
 * @param {{ role?: string, workLocation?: string, jobLevel?: string, industry?: string, level?: string }} raw
 */
function canonicalizeMarketInputs(raw = {}) {
  const rawRole = collapseWhitespace(raw.role || '');
  const rawLocation = collapseWhitespace(raw.workLocation || raw.location || '');
  const explicitLevel = collapseWhitespace(raw.jobLevel || raw.level || '');

  const canonicalRole = canonicalizeRoleTitle(rawRole);
  const canonicalCity = canonicalizeWorkLocation(rawLocation);
  const seniorityBand = extractSeniorityBandForCache(rawRole, explicitLevel);
  const industry = normalizeToken(raw.industry || '');

  return {
    rawRole,
    rawLocation,
    canonicalRole,
    canonicalCity,
    seniorityBand,
    industry,
    searchRole: displayCanonicalRole(canonicalRole) || rawRole,
    searchLocation: displayCanonicalCity(canonicalCity) || rawLocation,
    cacheKey: buildCanonicalMarketKey({
      role: rawRole,
      workLocation: rawLocation,
      jobLevel: explicitLevel,
      industry: raw.industry,
    }),
  };
}

module.exports = {
  ROLE_ABBREVIATION_TO_CANONICAL,
  canonicalizeRoleTitle,
  canonicalizeWorkLocation,
  extractSeniorityBand,
  extractSeniorityBandForCache,
  buildCanonicalMarketKey,
  canonicalizeMarketInputs,
  displayCanonicalRole,
  displayCanonicalCity,
};
